import { Component } from '@theme/component';

/**
 * Announcement banner custom element that rotates between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  #exitTimeout = undefined;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    this.addEventListener('shopify:block:deselect', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#motionPreference.addEventListener('change', this.#handleMotionChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    clearTimeout(this.#exitTimeout);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.suspend);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#motionPreference.removeEventListener('change', this.#handleMotionChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    this.suspend();
    if (!this.autoplay || this.#motionPreference.matches || document.hidden || this.matches(':hover') || this.contains(document.activeElement)) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;

    const relativeIndex = ((current % slides.length) + slides.length) % slides.length;
    if (relativeIndex === this.#current) return;

    const previousIndex = this.#current;
    this.#current = relativeIndex;
    clearTimeout(this.#exitTimeout);

    slides.forEach((slide, index) => {
      slide.toggleAttribute('data-exiting', index === previousIndex);
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
      slide.toggleAttribute('inert', index !== relativeIndex);
    });

    this.#exitTimeout = setTimeout(() => {
      slides.forEach((slide) => slide.removeAttribute('data-exiting'));
    }, 300);
  }

  #handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) queueMicrotask(() => this.resume());
  };

  #handleBlockSelect = (event) => {
    const slide = event.target.closest('slideshow-slide');
    const index = this.refs.slides?.indexOf(slide) ?? -1;
    if (index < 0) return;
    this.suspend();
    this.current = index;
  };

  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
  #handleMotionChange = () => (this.#motionPreference.matches ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
